import { Component, Input, OnInit } from '@angular/core';
import { IconsService } from '../services/icons.service';
import { formatContractDate, normalizeOptionalBoolean } from '../utils/contract-term.utils';
import { resolveSettlementFiscalAttributes } from '../utils/settlement-fiscal.utils';

@Component({
  selector: 'app-audit-trail-offcanvas',
  template: `
    <div class="offcanvas offcanvas-end" tabindex="-1" id="auditTrailOffcanvas" aria-labelledby="auditTrailOffcanvasLabel">
      <div class="offcanvas-header">
        <h5 class="offcanvas-title" id="auditTrailOffcanvasLabel">
          <fa-icon [icon]="icons.faHistory" class="me-2"></fa-icon>
          Historial de Cambios
        </h5>
        <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
      </div>
      <div class="offcanvas-body">
        <div *ngIf="loading" class="text-center">
          <fa-icon [icon]="icons.faSpinner" animation="spin" class="fa-2x"></fa-icon>
          <p class="mt-2">Cargando historial...</p>
        </div>
        
        <div *ngIf="!loading && auditData">
          <div class="mb-3">
            <small class="text-muted">
              <strong>ID:</strong> {{auditData.bill_id || auditData.id}}
              <span *ngIf="auditData.bill_reference"> | <strong>Referencia:</strong> {{auditData.bill_reference}}</span>
            </small>
            <br>
            <small class="text-muted">
              <strong>Total de cambios:</strong> {{auditData.total_changes}}
            </small>
          </div>
          
          <div class="timeline">
            <div *ngFor="let entry of auditData.audit_trail; let i = index" class="timeline-item">
              <div class="timeline-marker">
                <span class="badge rounded-pill" [ngClass]="getEventBadgeClass(entry.event)">
                  {{getEventLabel(entry.event)}}
                </span>
              </div>
              <div class="timeline-content">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h6 class="card-title mb-1">
                          {{getEventLabel(entry.event)}}
                        </h6>
                        <small class="text-muted">
                          <fa-icon [icon]="icons.faUser" class="me-1"></fa-icon>
                          {{entry.user_info?.name || 'Sistema'}}
                          <span *ngIf="entry.user_info?.type === 'user'"> (Usuario)</span>
                          <span *ngIf="entry.user_info?.type === 'anonymous'"> (Anónimo)</span>
                        </small>
                      </div>
                      <small class="text-muted">
                        <fa-icon [icon]="icons.faClock" class="me-1"></fa-icon>
                        {{entry.created_at | date: 'dd/MM/yyyy HH:mm'}}
                      </small>
                    </div>
                    
                    <div *ngIf="entry.object_snapshot && showSnapshots" class="mt-3">
                      <button 
                        class="btn btn-sm btn-outline-secondary" 
                        type="button" 
                        [attr.data-bs-toggle]="'collapse'" 
                        [attr.data-bs-target]="'#snapshot-' + entry.id"
                        [attr.aria-expanded]="false"
                        [attr.aria-controls]="'snapshot-' + entry.id"
                      >
                        <fa-icon [icon]="icons.faEye" class="me-1"></fa-icon>
                        Ver snapshot
                      </button>
                      <div class="collapse mt-2" [id]="'snapshot-' + entry.id">
                        <div class="card card-body bg-light">
                          <pre class="mb-0" style="font-size: 0.8em; max-height: 300px; overflow-y: auto;">{{entry.object_snapshot | json}}</pre>
                        </div>
                      </div>
                    </div>

                    <!-- Custom content slot for specific object types -->
                    <div *ngIf="entry.object_snapshot && customRenderer" class="mt-3">
                      <ng-container [ngSwitch]="objectType">
                        <div *ngSwitchCase="'gas_meter'" class="custom-snapshot">
                          <div class="row g-2" *ngIf="entry.object_snapshot">
                            <div class="col-6" *ngIf="entry.object_snapshot.name">
                              <small class="text-muted">Nombre:</small>
                              <div class="fw-bold">{{entry.object_snapshot.name}}</div>
                            </div>
                            <div class="col-6" *ngIf="entry.object_snapshot.meter_status">
                              <small class="text-muted">Estado:</small>
                              <div>
                                <span class="badge" [ngClass]="getMeterStatusClass(entry.object_snapshot.meter_status)">
                                  {{entry.object_snapshot.meter_status}}
                                </span>
                              </div>
                            </div>
                            <div class="col-6" *ngIf="entry.object_snapshot.brand">
                              <small class="text-muted">Marca:</small>
                              <div>{{entry.object_snapshot.brand}}</div>
                            </div>
                            <div class="col-6" *ngIf="entry.object_snapshot.meter_type">
                              <small class="text-muted">Tipo:</small>
                              <div>{{entry.object_snapshot.meter_type}}</div>
                            </div>
                            <div class="col-6" *ngIf="entry.object_snapshot.last_read">
                              <small class="text-muted">Última lectura:</small>
                              <div>{{entry.object_snapshot.last_read}}</div>
                            </div>
                            <div class="col-6" *ngIf="entry.object_snapshot.maintenance !== undefined">
                              <small class="text-muted">Mantenimiento:</small>
                              <div>
                                <span class="badge" [ngClass]="entry.object_snapshot.maintenance ? 'bg-warning' : 'bg-success'">
                                  {{entry.object_snapshot.maintenance ? 'En mantenimiento' : 'Activo'}}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div *ngSwitchCase="'bill'" class="custom-snapshot">
                          <div class="row g-2" *ngIf="entry.object_snapshot">
                            <div class="col-6" *ngIf="entry.object_snapshot.total">
                              <small class="text-muted">Total:</small>
                              <div class="fw-bold">{{entry.object_snapshot.total | currency}}</div>
                            </div>
                            <div class="col-6" *ngIf="entry.object_snapshot.state !== undefined">
                              <small class="text-muted">Estado:</small>
                              <div>
                                <span class="badge" [ngClass]="getBillStateClass(entry.object_snapshot.state)">
                                  {{getBillStateLabel(entry.object_snapshot.state)}}
                                </span>
                              </div>
                            </div>
                            <div class="col-6" *ngIf="entry.object_snapshot.consumo_m3">
                              <small class="text-muted">Consumo:</small>
                              <div>{{entry.object_snapshot.consumo_m3}} m³</div>
                            </div>
                            <div class="col-6" *ngIf="entry.object_snapshot.due_date">
                              <small class="text-muted">Vencimiento:</small>
                              <div>{{entry.object_snapshot.due_date | date: 'dd/MM/yyyy'}}</div>
                            </div>
                          </div>
                        </div>
                        <div *ngSwitchCase="'settlement'" class="custom-snapshot">
                          <div class="row g-2" *ngIf="entry.object_snapshot">
                            <div class="col-6" *ngIf="entry.object_snapshot.contract_term_years != null">
                              <small class="text-muted">Temporalidad del contrato:</small>
                              <div>{{entry.object_snapshot.contract_term_years}} años</div>
                            </div>
                            <div class="col-6" *ngIf="entry.object_snapshot.automatic_renewal != null">
                              <small class="text-muted">Renovación automática:</small>
                              <div>{{normalizeOptionalBoolean(entry.object_snapshot.automatic_renewal) ? 'Sí' : 'No'}}</div>
                            </div>
                            <div class="col-12" *ngIf="entry.object_snapshot.contract_end_date">
                              <small class="text-muted">Terminación del contrato:</small>
                              <div>{{formatContractDate(entry.object_snapshot.contract_end_date)}}</div>
                            </div>
                            <div class="col-12" *ngIf="resolveSettlementFiscalAttributes(entry.object_snapshot) as fiscal">
                              <small class="text-muted">Datos fiscales de la administración:</small>
                              <dl class="mb-0">
                                <dt>RFC</dt><dd>{{fiscal.rfc || '—'}}</dd>
                                <dt>Razón social</dt><dd>{{fiscal.razon_social || '—'}}</dd>
                                <dt>Código postal fiscal</dt><dd>{{fiscal.zip_code || '—'}}</dd>
                                <dt>Régimen fiscal</dt><dd>{{fiscal.regimen_fiscal || '—'}}</dd>
                                <dt>Uso CFDI</dt><dd>{{fiscal.uso_cfdi || '—'}}</dd>
                              </dl>
                            </div>
                            <div class="col-6" *ngIf="entry.object_snapshot.name">
                              <small class="text-muted">Nombre:</small>
                              <div class="fw-bold">{{entry.object_snapshot.name}}</div>
                            </div>
                            <div class="col-6" *ngIf="entry.object_snapshot.status !== undefined">
                              <small class="text-muted">Estado:</small>
                              <div>
                                <span class="badge" [ngClass]="getSettlementStatusClass(entry.object_snapshot.status)">
                                  {{getSettlementStatusLabel(entry.object_snapshot.status)}}
                                </span>
                              </div>
                            </div>
                            <div class="col-6" *ngIf="entry.object_snapshot.gas_price">
                              <small class="text-muted">Precio del gas:</small>
                              <div>{{entry.object_snapshot.gas_price | currency}}</div>
                            </div>
                            <div class="col-6" *ngIf="entry.object_snapshot.admin_price">
                              <small class="text-muted">Precio admin:</small>
                              <div>{{entry.object_snapshot.admin_price | currency}}</div>
                            </div>
                            <div class="col-12" *ngIf="entry.object_snapshot.address">
                              <small class="text-muted">Dirección:</small>
                              <div>{{entry.object_snapshot.address}}</div>
                            </div>
                          </div>
                        </div>
                        <div *ngSwitchDefault class="custom-snapshot">
                          <small class="text-muted">Datos del objeto:</small>
                          <pre class="mt-1" style="font-size: 0.8em; max-height: 200px; overflow-y: auto;">{{entry.object_snapshot | json}}</pre>
                        </div>
                      </ng-container>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div *ngIf="!loading && !auditData" class="text-center">
          <fa-icon [icon]="icons.faExclamationTriangle" class="fa-2x text-warning"></fa-icon>
          <p class="mt-2">No se encontró historial de cambios</p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .timeline {
      position: relative;
      padding-left: 1.5rem;
    }
    
    .timeline::before {
      content: '';
      position: absolute;
      left: 0.75rem;
      top: 0;
      bottom: 0;
      width: 2px;
      background-color: #dee2e6;
    }
    
    .timeline-item {
      position: relative;
      margin-bottom: 1.5rem;
    }
    
    .timeline-marker {
      position: absolute;
      left: -1.5rem;
      top: 0.5rem;
      z-index: 1;
    }
    
    .timeline-content {
      margin-left: 1rem;
    }
    
    .custom-snapshot {
      background-color: #f8f9fa;
      border-radius: 0.375rem;
      padding: 0.75rem;
      font-size: 0.875rem;
    }
    
    .card {
      border: 1px solid #dee2e6;
      box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    }
    
    .card-body {
      padding: 1rem;
    }
    
    pre {
      white-space: pre-wrap;
      word-wrap: break-word;
    }
  `]
})
export class AuditTrailOffcanvasComponent implements OnInit {
  readonly formatContractDate = formatContractDate;
  readonly normalizeOptionalBoolean = normalizeOptionalBoolean;
  readonly resolveSettlementFiscalAttributes = resolveSettlementFiscalAttributes;
  @Input() auditData: any = null;
  @Input() loading: boolean = false;
  @Input() objectType: string = 'generic'; // 'bill', 'tenant', 'building', etc.
  @Input() showSnapshots: boolean = true;
  @Input() customRenderer: boolean = false;

  constructor(public icons: IconsService) {}

  ngOnInit(): void {}

  getEventLabel(event: string): string {
    switch (event) {
      case 'create':
        return 'Creado';
      case 'update':
        return 'Actualizado';
      case 'destroy':
        return 'Eliminado';
      default:
        return event;
    }
  }

  getEventBadgeClass(event: string): string {
    switch (event) {
      case 'create':
        return 'bg-success';
      case 'update':
        return 'bg-info';
      case 'destroy':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  getBillStateLabel(state: number): string {
    switch (state) {
      case 0:
        return 'Borrador';
      case 1:
        return 'Emitido';
      case 3:
        return 'Cancelado';
      case 4:
        return 'Pendiente';
      default:
        return 'N/A';
    }
  }

  getBillStateClass(state: number): string {
    switch (state) {
      case 0:
        return 'bg-warning';
      case 1:
        return 'bg-success';
      case 3:
        return 'bg-danger';
      case 4:
        return 'bg-info';
      default:
        return 'bg-secondary';
    }
  }

  getMeterStatusClass(status: string): string {
    switch (status?.toUpperCase()) {
      case 'ACTIVO':
        return 'bg-success';
      case 'INACTIVO':
        return 'bg-warning';
      case 'MANTENIMIENTO':
        return 'bg-warning';
      default:
        return 'bg-secondary';
    }
  }

  getSettlementStatusLabel(status: number): string {
    switch (status) {
      case 1:
        return 'Activo';
      case 0:
      case 2:
        return 'Inactivo';
      default:
        return 'Desconocido';
    }
  }

  getSettlementStatusClass(status: number): string {
    switch (status) {
      case 1:
        return 'bg-success';
      case 0:
      case 2:
        return 'bg-warning';
      default:
        return 'bg-secondary';
    }
  }
}
